import { Component, HostListener } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Route } from '@rillion/components/topnav/topnav.component';
import { filter, map, Observable } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { UserDetailDialogComponent } from '@rillion/features/navigator/user-datail-dialog/user-detail-dialog.component';
import { AuthSelectors } from '@rillion/store/auth';
import { AuthActions } from '@rillion/store/auth/auth.actions';
import { Store } from '@ngrx/store';
import { NavigatorStore } from '@rillion/features/navigator/navigator/navigator.store';
import { RouterSelectors } from '@rillion/store/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SidebarComponent } from '../../../components/sidebar/sidebar.component';
import { TopnavComponent } from '../../../components/topnav/topnav.component';
import { LoadingScreenComponent } from '../../../core/loading-screen/loading-screen.component';
import { AsyncPipe } from '@angular/common';
import { InitializeService } from '@rillion/core/services/initialize.service';

@Component({
  standalone: true,
  selector: 'app-navigator',
  templateUrl: './navigator.component.html',
  styleUrl: './navigator.component.scss',
  providers: [NavigatorStore],
  imports: [LoadingScreenComponent, TopnavComponent, SidebarComponent, RouterOutlet, AsyncPipe],
})
export class NavigatorComponent {
  navOpen = false;
  user$ = this.store.select(AuthSelectors.selectUser);
  breadcrumbs$: Observable<Route[]>;
  isDesktop$ = this.store.select(RouterSelectors.selectRouterUrl).pipe(
    filter((url) => !!url),
    map((url) => !url?.includes('mobile')),
  );

  @HostListener('document:click', ['$event'])
  onWindowClick() {
    if (this.navOpen) this.navOpen = false;
  }

  constructor(
    private readonly store: Store,
    private readonly initializeService: InitializeService,
    public readonly componentStore: NavigatorStore,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private title: Title,
  ) {
    // Close the drawer on navigation
    this.router.events.pipe(takeUntilDestroyed()).subscribe(() => {
      this.navOpen = false;
    });

    this.breadcrumbs$ = this.router.events.pipe(
      takeUntilDestroyed(),
      filter((e) => e instanceof NavigationEnd),
      map(() => {
        return this.listChildRoutes(this.activatedRoute)
          .filter((route) => route.snapshot?.url.length)
          .filter((route, i, routes) => routes.indexOf(route) === i)
          .map((route) => {
            return {
              link: route.pathFromRoot
                .map((route) => route.snapshot.url)
                .filter((url) => !!url.length)
                .map(([url]) => url.path)
                .join('/'),
              text: route.snapshot?.data['breadcrumb']?.alias,
            };
          });
      }),
    );

    this.breadcrumbs$.pipe(takeUntilDestroyed()).subscribe((breadcrumbs) => {
      const breadcrumbsText = breadcrumbs.map(({ text }) => text).join('/');
      const appTitle = this.translateService.instant('Rillion Prime');
      this.title.setTitle(`${appTitle} ${breadcrumbsText}`);
    });

    this.isDesktop$.pipe(takeUntilDestroyed()).subscribe((isDesktop) => {
      if (isDesktop && this.initializeService.appComponentInitializing) {
        this.initializeService.setInitialized();
      }
    });
  }

  private listChildRoutes(route: ActivatedRoute): ActivatedRoute[] {
    return [route, ...route.children.map((route) => this.listChildRoutes(route)).flat()];
  }

  openUser() {
    this.dialog
      .open(UserDetailDialogComponent, {
        width: '20%',
        position: {
          top: '40px',
          right: '0px',
        },
        panelClass: 'custom-container',
      })
      .afterClosed()
      .pipe(
        map((role) => {
          if (role) {
            this.store.dispatch(AuthActions.setUserRole({ role }));
          }
        }),
      )
      .subscribe();
  }
}
